import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Globe, ChevronDown, Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { getSavedLanguage, saveLanguage } from "@/i18n";
import { ScrollArea } from "@/components/ui/scroll-area";

const languages = [
  { code: "en", name: "English", flag: "us" },
  { code: "de", name: "Deutsch", flag: "de" },
  { code: "es", name: "Español", flag: "es" },
  { code: "fr", name: "Français", flag: "fr" },
  { code: "it", name: "Italiano", flag: "it" },
  { code: "nl", name: "Nederlands", flag: "nl" },
  { code: "pl", name: "Polski", flag: "pl" },
  { code: "fi", name: "Suomi", flag: "fi" },
  { code: "sv", name: "Svenska", flag: "se" },
  { code: "nb", name: "Norsk Bokmål", flag: "no" },
  { code: "pt-BR", name: "Português (Brasil)", flag: "br" },
  { code: "zh", name: "简体中文", flag: "cn" },
  { code: "ja", name: "日本語", flag: "jp" },
] as const;

function detectBrowserLanguage(): string {
  const detector = new LanguageDetector();
  detector.init({ order: ["navigator"], caches: [] });
  const detected = detector.detect();
  const lang = Array.isArray(detected) ? detected[0] : detected;
  if (!lang) return "en";
  // Check for exact match first (e.g. "pt-BR")
  if (languages.some((l) => l.code === lang)) return lang;
  // Normalize e.g. "de-CH" -> "de"
  const base = lang.split("-")[0] ?? "en";
  return languages.some((l) => l.code === base) ? base : "en";
}

export function LanguageSwitcher({ mobile }: { mobile?: boolean }) {
  const { i18n, t } = useTranslation();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const isAuto = !getSavedLanguage();
  const resolvedLang = i18n.resolvedLanguage ?? i18n.language;
  const current = languages.find((l) => l.code === resolvedLang) ?? languages[0];

  const handleSelect = (code: string) => {
    saveLanguage(code);
    i18n.changeLanguage(code);
  };

  const handleAuto = () => {
    saveLanguage("auto");
    const browserLang = detectBrowserLanguage();
    i18n.changeLanguage(browserLang);
  };

  const currentLabel = isAuto ? "Auto" : current.name;

  const [showScrollHint, setShowScrollHint] = useState(true);
  const scrollCleanupRef = useRef<(() => void) | null>(null);

  const containerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    scrollCleanupRef.current?.();
    scrollCleanupRef.current = null;
    if (!node) return;
    const viewport = node.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;
    const check = () => {
      setShowScrollHint(
        viewport.scrollTop + viewport.clientHeight < viewport.scrollHeight - 8,
      );
    };
    check();
    viewport.addEventListener("scroll", check);
    scrollCleanupRef.current = () => viewport.removeEventListener("scroll", check);
  }, []);

  const filteredLanguages = languages.filter(
    (l) =>
      l.name.toLowerCase().includes(search.toLowerCase()) ||
      l.code.toLowerCase().includes(search.toLowerCase()),
  );
  const showAuto = search === "" || "auto".includes(search.toLowerCase());

  const trigger = mobile ? (
    <button
      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none"
      aria-label="Change language"
    >
      <Globe className="h-4 w-4" />
      <span className="flex-1 text-left">{currentLabel}</span>
      <ChevronDown className="h-3.5 w-3.5 opacity-50 mr-1" />
    </button>
  ) : (
    <button
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none"
      aria-label="Change language"
    >
      <Globe className="h-4 w-4" />
      <span className="hidden sm:inline uppercase">{current.code}</span>
      <ChevronDown className="h-3 w-3 opacity-50" />
    </button>
  );

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <DropdownMenu.Trigger asChild>
        {trigger}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 w-52 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
          sideOffset={5}
          align="end"
        >
          <div className="p-1 pb-0">
            <div className="flex items-center gap-1.5 rounded-sm border border-input px-2 py-1.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <input
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                placeholder={t("language.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <DropdownMenu.Separator className="my-1 h-px bg-border" />

          <div ref={containerCallbackRef} className="relative">
          <ScrollArea className="h-60">
            <div className="p-1 pt-0">
              {showAuto && (
                <DropdownMenu.Item
                  className={cn(
                    "relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                    isAuto && "bg-accent/50",
                  )}
                  onSelect={handleAuto}
                >
                  <Globe className="h-4 w-4" />
                  <span className="flex-1">Auto</span>
                  {isAuto && <Check className="h-3.5 w-3.5 text-muted-foreground" />}
                </DropdownMenu.Item>
              )}

              {showAuto && filteredLanguages.length > 0 && (
                <DropdownMenu.Separator className="my-1 h-px bg-border" />
              )}

              {filteredLanguages.map((lang) => (
                <DropdownMenu.Item
                  key={lang.code}
                  className={cn(
                    "relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                    !isAuto && lang.code === current.code && "bg-accent/50",
                  )}
                  onSelect={() => handleSelect(lang.code)}
                >
                  <span className={`fi fi-${lang.flag} rounded-sm`} />
                  <span className="flex-1">{lang.name}</span>
                  {!isAuto && lang.code === current.code && (
                    <Check className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </DropdownMenu.Item>
              ))}
            </div>
          </ScrollArea>
          {showScrollHint && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 rounded-b-md bg-linear-to-t from-popover to-transparent" />
          )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
